import type { Journey } from '@epilot/journey-logic-commons'
import {
  assignIdsToAllSteps,
  isLinearJourney,
  logicStringsToObjects
} from '@epilot/journey-logic-commons'
import { useCallback, useEffect, useMemo, useReducer } from 'react'

import { selectOriginalJourneyPerId } from '../../store/selectors/config'
import { selectInitialStepIndex } from '../../store/selectors/runtime'
import { useAppManagerContext } from '../useAppManagerContext'
import { useAppStoreSelector } from '../useAppStoreSelector'
import { useCheckNonce } from '../useCheckNonce'
import { useGetJourneyParams } from '../useGetJourneyParams'
import type { DataInjectionOptions } from '../useMessageHandler/types'

import {
  initialReducerState,
  reducer,
  setConfigJourneySuccessAction,
  setMultipleJourneyStateAction
} from './utils'

/** @deprecated should be removed soon */
export const useGetJourney = ({
  journeyIdOverride
}: {
  journeyIdOverride?: string | null
}) => {
  const [configState, dispatchConfigStateChange] = useReducer(
    reducer,
    initialReducerState
  )

  const {
    appParams: { journeyId: paramJourneyId, preview }
  } = useAppManagerContext()
  const { contextData, dataInjectionOptions } = useGetJourneyParams()

  const journeyId = journeyIdOverride ?? paramJourneyId

  const originalJourney = useAppStoreSelector((state) =>
    selectOriginalJourneyPerId(state, journeyId)
  )

  const initialStepIndex = useAppStoreSelector(selectInitialStepIndex)

  const { isNonceUsed } = useCheckNonce(contextData?.nonce)

  useEffect(() => {
    // load journey if no nonce provided, or if nonce is provided and is not used
    if (!contextData?.nonce || isNonceUsed === false) {
      dispatchConfigStateChange(setConfigJourneySuccessAction(originalJourney))
    }
  }, [originalJourney, contextData?.nonce, isNonceUsed])

  useEffect(() => {
    if (!preview || initialStepIndex === undefined) return

    dispatchConfigStateChange(
      setMultipleJourneyStateAction({
        stepIndex: String(initialStepIndex)
      })
    )
  }, [preview, initialStepIndex])

  const setJourney = useCallback(
    (newJourney: Journey) =>
      dispatchConfigStateChange(setConfigJourneySuccessAction(newJourney)),
    [dispatchConfigStateChange]
  )

  const journey = useMemo(() => {
    // underlying logic is currently mutating the journey object, therefore
    // we need to clone it before passing down
    // TODO: fix underlying parts (i.e. dynamic pricing)
    const data = structuredClone(configState.journeyConfig)

    return data?.steps ? assignIdsToAllSteps(data) : data
  }, [configState.journeyConfig])

  // WARNING: expensive operation, shouldn't run on every render
  const isLinear = useMemo(
    () =>
      isLinearJourney(journey?.steps, logicStringsToObjects(journey?.logics)),
    [journey?.steps, journey?.logics]
  )

  // decode base64 string dataInjectionOptions
  const dataInjectionOptionsParam: DataInjectionOptions | undefined =
    useMemo(() => {
      try {
        if (dataInjectionOptions) {
          return JSON.parse(atob(dataInjectionOptions))
        }
      } catch {
        return undefined
      }
    }, [dataInjectionOptions])

  return {
    isLoading: configState.isLoading,
    stepIndex: configState.stepIndex ? Number(configState.stepIndex) : 0,
    journey,
    initialState: configState.initialState,
    preview,
    journeyId,
    contextData,
    isLinearJourney: isLinear,
    setJourney,
    dataInjectionOptionsParam: dataInjectionOptionsParam,
    isNonceUsed
  }
}
